import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LanguageIcon from '@mui/icons-material/Language'

import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { baseUrl } from 'api/config'
import { useSearchData } from 'api/search'
import { VERTICAL_OPTIONS } from 'components/DataGrid/prototype/config/options'
import React, { useState } from 'react'
import { Building2Icon, CalendarIcon, LayersIcon, SERVICE_LINE_COLORS, SERVICE_LINE_ICONS } from './icons'
import { useRoadmap } from './RoadmapContext'
import { ENGAGEMENT_PRESETS, EngagementDuration } from './types'

const RoadmapSetup: React.FC = () => {
  const theme = useTheme()
  const { state, dispatch, serviceLineSummaries, ratePerPoint } = useRoadmap()
  const { engagementConfig, clientName, enabledServiceLines } = state
  const { menu_orgs: organizations = [] } = useSearchData()
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisSummary, setAnalysisSummary] = useState('')
  const canContinue = clientName.trim().length > 0 && enabledServiceLines.size > 0

  const handleAnalyzeWebsite = async () => {
    const url = state.clientWebsite.trim()
    if (!url) return
    setIsAnalyzing(true)
    setAnalysisSummary('')
    try {
      const resp = await fetch(`${baseUrl}/api/agent/analyze-website`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      const json = await resp.json()
      if (json.success && json.data) {
        const d = json.data
        if (d.industry) dispatch({ type: 'SET_CLIENT_INDUSTRY', industry: d.industry })
        if (d.businessModel) dispatch({ type: 'SET_BUSINESS_MODEL', model: d.businessModel })
        if (d.clientSize) dispatch({ type: 'SET_CLIENT_SIZE', size: d.clientSize })
        if (d.suggestedObjectives?.length) {
          for (const obj of d.suggestedObjectives) {
            if (!state.objectives.has(obj)) dispatch({ type: 'TOGGLE_OBJECTIVE', objective: obj })
          }
        }
        if (d.summary) setAnalysisSummary(d.summary)
        if (d.salesNotes && !state.salesNotes) dispatch({ type: 'SET_SALES_NOTES', notes: d.salesNotes })
        if (d.aiInstructions && !state.aiInstructions)
          dispatch({ type: 'SET_AI_INSTRUCTIONS', instructions: d.aiInstructions })
      }
    } catch (err) {
      console.error('Website analysis failed:', err)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Compute total engagement summary
  const totalSetupPts = engagementConfig.includeSetup ? engagementConfig.setupBudget : 0
  const totalMonthlyPts = engagementConfig.monthlyBudget * engagementConfig.duration
  const totalPts = totalSetupPts + totalMonthlyPts
  const totalInvestment = totalPts * ratePerPoint

  return (
    // <Box sx={{ maxWidth: 960, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
    <Box sx={{ maxWidth: 800, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      {/* Client Profile Section */}
      <Section
        icon={<Building2Icon sx={{ color: 'icon.roadmap_icon' }} />}
        title="Client Profile"
        subtitle="Tell us about the client to tailor recommendations"
      >
        {/* Row 1: Client Name + Industry */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <Autocomplete
            options={organizations}
            getOptionLabel={(option: any) => (typeof option === 'string' ? option : option.name || '')}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options
              const q = inputValue.toLowerCase()
              return options.filter((o: any) => (o.name || '').toLowerCase().includes(q))
            }}
            value={
              organizations.find((o: any) => o.org_uuid === state.clientOrgId) ||
              (state.clientOrgId && state.clientName ? { org_uuid: state.clientOrgId, name: state.clientName } : null)
            }
            onChange={(_, newValue: any) => {
              if (newValue) {
                // console.log('newValue', newValue)
                const vertical = Array.isArray(newValue.vertical) ? newValue.vertical[0] : newValue.vertical || ''
                const industry =
                  vertical && vertical !== 'No Vertical Assigned'
                    ? vertical
                    : newValue?.industry
                    ? newValue.industry
                    : ''
                dispatch({
                  type: 'SET_CLIENT_ORG',
                  name: newValue.name || '',
                  orgId: newValue.org_uuid || '',
                  industry,
                })
                if (newValue.website && !state.clientWebsite) {
                  dispatch({ type: 'SET_CLIENT_WEBSITE', website: newValue.website })
                }
              } else {
                dispatch({ type: 'RESET_CLIENT_PROFILE' })
              }
            }}
            renderOption={(props, option: any) => (
              <Box
                component="li"
                {...props}
                key={
                  option?.b2d_account_id ? `${option?.b2d_account_id}::${option?.PK}` : `${option?.PK}::${option?.SK}`
                }
              >
                {/* <RenderOption props={props} option={option} /> */}
                {option?.name ?? ''}
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Client / Company"
                required
                size="small"
                placeholder="Search Client / Company"
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: 16,
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option: any, val: any) => option?.org_uuid === val?.org_uuid}
            freeSolo
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                dispatch({ type: 'SET_CLIENT_NAME', name: value })
              }
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="Industry / Vertical"
            value={state.clientIndustry}
            onChange={e => dispatch({ type: 'SET_CLIENT_INDUSTRY', industry: e.target.value })}
            size="small"
            placeholder="Select Industry / Vertical"
            sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 37 } }}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
            InputLabelProps={{
              shrink: true,
              sx: {
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {VERTICAL_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {/* Row 2: Website + Analysis */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
          <TextField
            fullWidth
            label="Client Website"
            placeholder="e.g. acme.com"
            value={state.clientWebsite}
            onChange={e => dispatch({ type: 'SET_CLIENT_WEBSITE', website: e.target.value })}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleAnalyzeWebsite()
            }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={!state.clientWebsite.trim() || isAnalyzing}
            onClick={handleAnalyzeWebsite}
            sx={{ whiteSpace: 'nowrap', minWidth: 140, px: 4, height: 37 }}
          >
            {isAnalyzing ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <LanguageIcon sx={{ fontSize: 16, mr: 0.5 }} />
            )}
            {isAnalyzing ? 'Analyzing...' : 'Fetch & Analyze'}
          </Button>
        </Box>
        {analysisSummary && (
          <Typography variant="caption" sx={{ color: 'primary.main', mb: 2, display: 'block', fontStyle: 'italic' }}>
            {analysisSummary}
          </Typography>
        )}
        {/* Row 3: Business Model + Client Size */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Business Model
            </Typography>
            <ToggleButtonGroup
              value={state.businessModel}
              exclusive
              onChange={(_, val) => val && dispatch({ type: 'SET_BUSINESS_MODEL', model: val })}
              size="small"
            >
              <ToggleButton value="lead_gen" sx={toggleBtnSx}>
                Lead Gen
              </ToggleButton>
              <ToggleButton value="ecommerce" sx={toggleBtnSx}>
                E-Commerce
              </ToggleButton>
              <ToggleButton value="both" sx={toggleBtnSx}>
                Both
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Client Size
            </Typography>
            <ToggleButtonGroup
              value={state.clientSize}
              exclusive
              onChange={(_, val) => val && dispatch({ type: 'SET_CLIENT_SIZE', size: val })}
              size="small"
            >
              <ToggleButton value="smb" sx={toggleBtnSx}>
                SMB
              </ToggleButton>
              <ToggleButton value="mid_market" sx={toggleBtnSx}>
                Mid-Market
              </ToggleButton>
              <ToggleButton value="enterprise" sx={toggleBtnSx}>
                Enterprise
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Key Objectives */}
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, mb: 0.5, display: 'block' }}>
          Key Objectives
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {KEY_OBJECTIVES.map(obj => {
            const isActive = state.objectives.has(obj.value)
            return (
              <Chip
                key={obj.value}
                label={obj.label}
                icon={<Typography sx={{ fontSize: 14, pl: 0.5 }}>{obj.icon}</Typography>}
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => dispatch({ type: 'TOGGLE_OBJECTIVE', objective: obj.value })}
                sx={{
                  borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive ? alpha(theme.palette.text.highlight, 0.15) : 'transparent',
                  color: isActive ? 'primary.main' : 'text.secondary',
                  fontWeight: isActive ? 600 : 400,
                  '&:hover': { bgcolor: alpha(theme.palette.text.highlight, 0.08) },
                }}
              />
            )
          })}
        </Box>
      </Section>

      {/* Sales Intelligence Section */}
      <Section
        icon={<InfoOutlinedIcon sx={{ color: 'icon.roadmap_icon' }} />}
        title="Sales Intelligence"
        subtitle="Add notes, transcripts, or upload documents to help AI build a better roadmap"
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Sales Notes"
          placeholder="Paste call transcripts, meeting notes, client pain points, competitive context..."
          value={state.salesNotes}
          onChange={e => dispatch({ type: 'SET_SALES_NOTES', notes: e.target.value })}
          size="small"
          sx={{ mb: 2 }}
          InputLabelProps={{
            shrink: true,
            sx: { '& .MuiFormLabel-asterisk': { display: 'none' } },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            AI Instructions <em>(optional)</em>
          </Typography>
        </Box>
        <TextField
          fullWidth
          placeholder="e.g., Focus heavily on SEO in the first 3 months, allocate at least 30% to content, avoid paid media items..."
          value={state.aiInstructions}
          onChange={e => dispatch({ type: 'SET_AI_INSTRUCTIONS', instructions: e.target.value })}
          size="small"
        />
      </Section>

      {/* Engagement Plan Section */}
      <Section
        icon={<CalendarIcon sx={{ color: 'icon.roadmap_icon' }} />}
        title="Engagement Plan"
        subtitle="Define the duration and budget for this engagement"
      >
        {/* Duration Cards */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Engagement Duration
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          {ENGAGEMENT_PRESETS.map(preset => {
            const isSelected = engagementConfig.duration === preset.duration
            return (
              <Box
                key={preset.duration}
                onClick={() => dispatch({ type: 'SET_DURATION', duration: preset.duration as EngagementDuration })}
                sx={{
                  flex: '1 1 160px',
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? alpha(theme.palette.text.highlight, 0.08) : 'background.paper',
                  transition: 'all 0.2s',
                  position: 'relative',
                  '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.text.highlight, 0.04) },
                }}
              >
                {preset.badge && (
                  <Chip
                    label={preset.badge}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 20,
                      fontSize: 10,
                      bgcolor: preset.badge === 'Recommended' ? 'success.main' : 'primary.main',
                      color: '#000',
                      fontWeight: 700,
                    }}
                  />
                )}
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {preset.duration}{' '}
                  <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                    mos
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {preset.label}
                </Typography>
                <Typography variant="caption" sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }}>
                  ${preset.ratePerPoint}/pt
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {preset.description}
                </Typography>
              </Box>
            )
          })}
        </Box>

        {/* Budget Configuration */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Start Month"
            type="month"
            size="small"
            value={engagementConfig.startMonth}
            onChange={e => dispatch({ type: 'SET_ENGAGEMENT', config: { startMonth: e.target.value } })}
            sx={{ width: 170 }}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Discount
            </Typography>
            <TextField
              size="small"
              type="number"
              value={engagementConfig.discountPercent}
              onChange={e =>
                dispatch({
                  type: 'SET_ENGAGEMENT',
                  config: { discountPercent: Math.max(0, Math.min(100, Number(e.target.value))) },
                })
              }
              sx={{ width: 80 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          </Box>
          <ToggleButtonGroup
            value={engagementConfig.budgetMode}
            exclusive
            onChange={(_, val) => {
              if (!val) return
              const rate = ratePerPoint
              if (val === 'dollars') {
                dispatch({
                  type: 'SET_ENGAGEMENT',
                  config: {
                    budgetMode: 'dollars',
                    monthlyDollarBudget: engagementConfig.monthlyBudget * rate,
                    setupDollarBudget: engagementConfig.setupBudget * rate,
                  },
                })
              } else {
                dispatch({
                  type: 'SET_ENGAGEMENT',
                  config: {
                    budgetMode: 'points',
                    monthlyBudget: Math.round(engagementConfig.monthlyDollarBudget / rate),
                    setupBudget: Math.round(engagementConfig.setupDollarBudget / rate),
                  },
                })
              }
            }}
            size="small"
          >
            <ToggleButton value="points" sx={toggleBtnSx}>
              Points
            </ToggleButton>
            <ToggleButton value="dollars" sx={toggleBtnSx}>
              $ Budget
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ ml: 'auto' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Effective Rate
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
              ${ratePerPoint * (1 - engagementConfig.discountPercent / 100)}/pt
            </Typography>
          </Box>
        </Box>

        {/* Setup Phase Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={engagementConfig.includeSetup}
              onChange={(_, checked) => dispatch({ type: 'SET_ENGAGEMENT', config: { includeSetup: checked } })}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              Include Setup Phase{' '}
              <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                Recommended for multi-month engagements
              </Typography>
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {/* Budget Inputs */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {engagementConfig.includeSetup && (
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label={engagementConfig.budgetMode === 'dollars' ? 'Setup Budget ($)' : 'Setup Budget (pts)'}
                type="number"
                size="small"
                value={
                  engagementConfig.budgetMode === 'dollars'
                    ? engagementConfig.setupDollarBudget
                    : engagementConfig.setupBudget
                }
                onChange={e => {
                  const val = Number(e.target.value) || 0
                  if (engagementConfig.budgetMode === 'dollars') {
                    dispatch({
                      type: 'SET_ENGAGEMENT',
                      config: { setupDollarBudget: val, setupBudget: Math.round(val / ratePerPoint) },
                    })
                  } else {
                    dispatch({ type: 'SET_ENGAGEMENT', config: { setupBudget: val } })
                  }
                }}
                InputProps={{
                  startAdornment:
                    engagementConfig.budgetMode === 'dollars' ? (
                      <InputAdornment position="start">$</InputAdornment>
                    ) : undefined,
                }}
              />
              <Typography variant="caption" sx={{ color: 'primary.main', mt: 0.5, display: 'block' }}>
                ~ {engagementConfig.setupBudget} points at ${ratePerPoint}/pt
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label={engagementConfig.budgetMode === 'dollars' ? 'Monthly Budget ($)' : 'Monthly Budget (pts)'}
              type="number"
              size="small"
              value={
                engagementConfig.budgetMode === 'dollars'
                  ? engagementConfig.monthlyDollarBudget
                  : engagementConfig.monthlyBudget
              }
              onChange={e => {
                const val = Number(e.target.value) || 0
                if (engagementConfig.budgetMode === 'dollars') {
                  dispatch({
                    type: 'SET_ENGAGEMENT',
                    config: { monthlyDollarBudget: val, monthlyBudget: Math.round(val / ratePerPoint) },
                  })
                } else {
                  dispatch({ type: 'SET_ENGAGEMENT', config: { monthlyBudget: val } })
                }
              }}
              InputProps={{
                startAdornment:
                  engagementConfig.budgetMode === 'dollars' ? (
                    <InputAdornment position="start">$</InputAdornment>
                  ) : undefined,
                endAdornment: <InputAdornment position="end">/mo</InputAdornment>,
              }}
            />
            <Typography variant="caption" sx={{ color: 'primary.main', mt: 0.5, display: 'block' }}>
              ~ {engagementConfig.monthlyBudget} points/mo at ${ratePerPoint}/pt
            </Typography>
          </Box>
        </Box>

        {/* Total Summary */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            bgcolor: alpha(theme.palette.text.highlight, 0.06),
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Total engagement: <b>{totalSetupPts}</b> setup + <b>{engagementConfig.monthlyBudget}</b> x{' '}
            {engagementConfig.duration} months = <b>{totalPts}</b> points
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5, textAlign: 'right' }}>
            Total investment: ${totalInvestment.toLocaleString()}
          </Typography>
        </Box>
      </Section>

      {/* Service Lines Section */}
      <Section
        icon={<LayersIcon sx={{ color: 'icon.roadmap_icon' }} />}
        title="Service Lines"
        subtitle="Activate the service lines to include in this package"
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {serviceLineSummaries.map(sl => {
            const isEnabled = enabledServiceLines.has(sl.code)
            const SlIcon = SERVICE_LINE_ICONS[sl.code]
            const slColor = SERVICE_LINE_COLORS[sl.code] || '#DAA520'
            return (
              <Box
                key={sl.code}
                onClick={() => dispatch({ type: 'TOGGLE_SERVICE_LINE', code: sl.code })}
                sx={{
                  flex: '1 1',
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isEnabled ? slColor : 'divider',
                  bgcolor: isEnabled ? `${slColor}15` : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: slColor },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  {SlIcon && (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${slColor}22`,
                      }}
                    >
                      <SlIcon sx={{ fontSize: 18, color: slColor }} />
                    </Box>
                  )}
                  <Switch checked={isEnabled} size="small" color="primary" sx={{ p: 0 }} readOnly />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {sl.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {sl.itemCount} items
                </Typography>
                <br />
                <Typography variant="caption" sx={{ color: 'primary.main' }}>
                  {sl.minPoints}–{sl.maxPoints} pts each
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Section>

      {/* Continue Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {canContinue
            ? `${enabledServiceLines.size} service line${enabledServiceLines.size > 1 ? 's' : ''} selected`
            : 'Enter a client name and select at least one service line to continue'}
        </Typography>
        <Button
          variant="contained"
          disabled={!canContinue}
          onClick={() => dispatch({ type: 'SET_VIEW', view: 'builder' })}
          sx={{ px: 4 }}
        >
          Start Building
        </Button>
      </Box>
    </Box>
  )
}

// Section wrapper matching TED Toolbox card style
function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // bgcolor: 'rgba(74, 161, 123, 0.15)',
            bgcolor: 'icon.roadmap_icon_bg',
            color: 'icon.roadmap_icon',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

const toggleBtnSx = { textTransform: 'none', fontSize: 12, px: 1.5 } as const

const KEY_OBJECTIVES: { value: string; label: string; icon: string }[] = [
  { value: 'grow_organic_traffic', label: 'Grow Organic Traffic', icon: '↗' },
  { value: 'improve_paid_performance', label: 'Improve Paid Performance', icon: '◎' },
  { value: 'increase_conversion_rate', label: 'Increase Conversion Rate', icon: '⚡' },
  { value: 'build_brand_awareness', label: 'Build Brand Awareness', icon: '◉' },
  { value: 'expand_new_markets', label: 'Expand to New Markets', icon: '🌐' },
  { value: 'develop_content_strategy', label: 'Develop Content Strategy', icon: '✎' },
  { value: 'fix_technical_issues', label: 'Fix Technical Issues', icon: '🔧' },
  { value: 'better_analytics', label: 'Better Analytics & Insights', icon: '📊' },
  { value: 'gain_competitive_edge', label: 'Gain Competitive Edge', icon: '✦' },
  { value: 'improve_retention_ltv', label: 'Improve Retention & LTV', icon: '♡' },
]

export default RoadmapSetup
